import { baseLegendKey } from "@/lib/domains";
import {
  deleteImportedPrepDeck,
  listImportedPrepDecks,
  listPrepPlans,
  saveDeck,
  savePrepPlan,
  type ImportedPrepDeck,
  type PrepSidePlan,
  type SavedDeck,
  type SavedDeckEntry,
} from "@/lib/local-store";
import type { CardRecord, DeckEntry } from "@/lib/types";

export type ParsedImportEntry = {
  cardCode: string;
  name: string;
  quantity: number;
  type?: string | null;
};

export type ParsedImportDeck = {
  mainDeck: ParsedImportEntry[];
  sideboard: ParsedImportEntry[];
  chosenChampion: string | null;
};

export function normalizeImportedName(name: string) {
  return name
    .toLowerCase()
    .replace(/\s*\([^)]*\)\s*/gu, " ")
    .replace(/[’']/gu, "")
    .replace(/\s*[-,:]\s*/gu, " ")
    .replace(/\s+/gu, " ")
    .trim();
}

function inferImportedType(
  cardCode: string,
  name: string,
  cardsByName: Map<string, CardRecord>,
) {
  const local = cardsByName.get(normalizeImportedName(name));
  if (local?.type) return local.type;
  if (/-[Rr]\d+/u.test(cardCode) || /\brune\b/iu.test(name)) return "Rune";
  return null;
}

export function parseImportedDeckList(
  raw: string,
  cardsByName: Map<string, CardRecord>,
): ParsedImportDeck {
  const mainDeck: ParsedImportEntry[] = [];
  const sideboard: ParsedImportEntry[] = [];
  let chosenChampion: string | null = null;
  let section: "legend" | "champion" | "main" | "battlefields" | "runes" | "sideboard" = "main";

  function pushEntry(quantity: number, name: string, explicitCode?: string) {
    const local = cardsByName.get(normalizeImportedName(name));
    const type =
      local?.type ||
      (section === "legend"
        ? "Legend"
        : section === "battlefields"
          ? "Battlefield"
          : section === "runes"
            ? "Rune"
            : inferImportedType(explicitCode ?? local?.id ?? name, name, cardsByName));
    const cardCode = explicitCode?.trim().toUpperCase() || local?.id || normalizeImportedName(name);
    const entry = { cardCode, name, quantity, type };

    if (!chosenChampion && (section === "legend" || type === "Legend")) chosenChampion = name;
    if (section === "champion" && !chosenChampion) chosenChampion = name;
    if (section === "sideboard") sideboard.push(entry);
    else mainDeck.push(entry);
  }

  for (const rawLine of raw.split(/\r?\n/u)) {
    const line = rawLine.trim();
    if (!line) continue;

    if (/^legend\b/iu.test(line)) {
      section = "legend";
      continue;
    }
    if (/^champion\b/iu.test(line)) {
      section = "champion";
      continue;
    }
    if (/^sideboard\b/iu.test(line)) {
      section = "sideboard";
      continue;
    }
    if (/^battlefields?\b/iu.test(line)) {
      section = "battlefields";
      continue;
    }
    if (/^runes?\b/iu.test(line)) {
      section = "runes";
      continue;
    }
    if (/^(main|main deck|deck)\b/iu.test(line)) {
      section = "main";
      continue;
    }

    const withCode = line.match(/^(\d+)\s+(.+?)\s+\(([A-Z0-9-]+)\)$/u);
    if (withCode) {
      pushEntry(Number(withCode[1]), withCode[2].trim(), withCode[3].trim());
      continue;
    }

    const noCode = line.match(/^(\d+)\s+(.+)$/u);
    if (noCode) {
      pushEntry(Number(noCode[1]), noCode[2].trim());
    }
  }

  return { mainDeck, sideboard, chosenChampion };
}

function buildCardIndexes(cards: CardRecord[]) {
  const byName = new Map<string, CardRecord>();
  const bySetNum = new Map<string, CardRecord>();
  const byRiftPrefix = new Map<string, CardRecord>();

  for (const card of cards) {
    byName.set(normalizeImportedName(card.name), card);
    if (card.cleanName) byName.set(normalizeImportedName(card.cleanName), card);
    if (card.setId != null && card.collectorNumber != null) {
      bySetNum.set(`${card.setId.toUpperCase()}-${card.collectorNumber}`, card);
      bySetNum.set(
        `${card.setId.toUpperCase()}-${String(card.collectorNumber).padStart(3, "0")}`,
        card,
      );
    }
    if (card.riftboundId) {
      const parts = card.riftboundId.toLowerCase().split("-");
      if (parts.length >= 2) {
        byRiftPrefix.set(`${parts[0]}-${String(Number(parts[1])).padStart(3, "0")}`, card);
        byRiftPrefix.set(`${parts[0]}-${Number(parts[1])}`, card);
      }
    }
  }

  return { byName, bySetNum, byRiftPrefix };
}

export function resolveImportedCard(
  entry: ParsedImportEntry,
  indexes: ReturnType<typeof buildCardIndexes>,
): CardRecord | null {
  const code = entry.cardCode?.trim().toUpperCase();
  if (code) {
    const m = code.match(/^([A-Z]+)[-_]?0*(\d+)$/u);
    if (m) {
      const key = `${m[1]}-${Number(m[2])}`;
      const padded = `${m[1]}-${String(Number(m[2])).padStart(3, "0")}`;
      const hit = indexes.bySetNum.get(key) || indexes.bySetNum.get(padded)
        || indexes.byRiftPrefix.get(key.toLowerCase())
        || indexes.byRiftPrefix.get(padded.toLowerCase());
      if (hit) return hit;
    }
  }
  return indexes.byName.get(normalizeImportedName(entry.name)) ?? null;
}

function toDeckEntries(
  parsed: ParsedImportEntry[],
  indexes: ReturnType<typeof buildCardIndexes>,
): { entries: DeckEntry[]; unresolved: string[] } {
  const unresolved: string[] = [];
  const byId = new Map<string, DeckEntry>();

  for (const row of parsed) {
    const card = resolveImportedCard(row, indexes);
    if (!card) {
      unresolved.push(row.name);
      continue;
    }
    const existing = byId.get(card.id);
    if (existing) existing.quantity += row.quantity;
    else byId.set(card.id, { card, quantity: row.quantity });
  }

  return { entries: [...byId.values()], unresolved };
}

export type ImportDeckOptions = {
  name?: string;
  id?: string;
};

/** Convert a pasted decklist into a SavedDeck (shared Decks + Prepa). */
export function importDeckListToSavedDeck(
  raw: string,
  cards: CardRecord[],
  nameOrOpts?: string | ImportDeckOptions,
): { deck: SavedDeck; unresolved: string[] } {
  const opts: ImportDeckOptions =
    typeof nameOrOpts === "string" ? { name: nameOrOpts } : (nameOrOpts ?? {});
  const indexes = buildCardIndexes(cards);
  const parsed = parseImportedDeckList(raw, indexes.byName);

  if (parsed.mainDeck.length === 0 && parsed.sideboard.length === 0) {
    throw new Error("Aucune ligne valide trouvée. Format: `3 Nom de carte (VEN-123)`.");
  }

  const unresolved: string[] = [];
  const main = toDeckEntries(parsed.mainDeck, indexes);
  const side = toDeckEntries(parsed.sideboard, indexes);
  unresolved.push(...main.unresolved, ...side.unresolved);

  const legend =
    main.entries.find((e) => e.card.type === "Legend")?.card ||
    (parsed.chosenChampion
      ? indexes.byName.get(normalizeImportedName(parsed.chosenChampion))
      : null) ||
    null;

  if (!legend) {
    throw new Error("Impossible de trouver la légende dans la liste importée.");
  }

  const battlefieldIds = main.entries
    .filter((e) => e.card.type === "Battlefield")
    .flatMap((e) => Array.from({ length: e.quantity }, () => e.card.id));

  const runeEntries: SavedDeckEntry[] = main.entries
    .filter((e) => e.card.type === "Rune")
    .map((e) => ({ cardId: e.card.id, quantity: e.quantity }));

  const playEntries = main.entries.filter(
    (e) => e.card.type !== "Legend" && e.card.type !== "Battlefield" && e.card.type !== "Rune",
  );

  const champion =
    playEntries.find((e) => {
      if (!parsed.chosenChampion) return false;
      return (
        normalizeImportedName(e.card.name) === normalizeImportedName(parsed.chosenChampion) ||
        baseLegendKey(e.card.name) === baseLegendKey(parsed.chosenChampion)
      );
    })?.card ??
    playEntries.find((e) => e.card.supertype?.toLowerCase().includes("champion"))?.card ??
    null;

  const deck = saveDeck({
    id: opts.id,
    name:
      opts.name?.trim() ||
      (parsed.chosenChampion
        ? `Import ${parsed.chosenChampion}`
        : `Deck importé ${new Date().toLocaleDateString("fr-FR")}`),
    legend,
    championId: champion?.id ?? null,
    entries: playEntries,
    battlefieldIds,
    sideboard: side.entries,
    runeEntries,
  });

  return { deck, unresolved };
}

function formatImportedEntriesAsList(deck: ImportedPrepDeck): string {
  const lines: string[] = [];
  if (deck.chosenChampion) {
    lines.push("Legend:", `1 ${deck.chosenChampion}`, "", "MainDeck:");
  }
  for (const entry of deck.mainDeck) {
    lines.push(
      entry.cardCode && entry.cardCode !== normalizeImportedName(entry.name)
        ? `${entry.quantity} ${entry.name} (${entry.cardCode})`
        : `${entry.quantity} ${entry.name}`,
    );
  }
  if (deck.sideboard.length > 0) {
    lines.push("", "Sideboard:");
    for (const entry of deck.sideboard) {
      lines.push(
        entry.cardCode && entry.cardCode !== normalizeImportedName(entry.name)
          ? `${entry.quantity} ${entry.name} (${entry.cardCode})`
          : `${entry.quantity} ${entry.name}`,
      );
    }
  }
  return lines.join("\n");
}

function buildImportedCodeToIdMap(deck: ImportedPrepDeck, cards: CardRecord[]) {
  const indexes = buildCardIndexes(cards);
  const map = new Map<string, string>();
  for (const entry of [...deck.mainDeck, ...deck.sideboard]) {
    const card = resolveImportedCard(entry, indexes);
    if (!card) continue;
    map.set(entry.cardCode, card.id);
    map.set(normalizeImportedName(entry.name), card.id);
    map.set(entry.name, card.id);
  }
  return map;
}

function remapSidePlanIds(plan: PrepSidePlan, codeToId: Map<string, string>): PrepSidePlan {
  const remap = (entries: SavedDeckEntry[]) =>
    entries.map((entry) => ({
      ...entry,
      cardId:
        codeToId.get(entry.cardId) ||
        codeToId.get(normalizeImportedName(entry.cardId)) ||
        entry.cardId,
    }));
  return { sideIn: remap(plan.sideIn), sideOut: remap(plan.sideOut) };
}

/** One-shot: move legacy Prepa-only imports into shared SavedDeck list. */
export function migrateImportedPrepDecksToSaved(cards: CardRecord[]): number {
  if (typeof window === "undefined" || cards.length === 0) return 0;
  const imported = listImportedPrepDecks();
  if (imported.length === 0) return 0;

  let migrated = 0;
  for (const old of imported) {
    try {
      const raw = old.sourceCode?.trim() || formatImportedEntriesAsList(old);
      const { deck } = importDeckListToSavedDeck(raw, cards, { name: old.name, id: old.id });
      const codeToId = buildImportedCodeToIdMap(old, cards);
      for (const prep of listPrepPlans().filter((p) => p.deckId === old.id)) {
        savePrepPlan({
          id: prep.id,
          deckId: deck.id,
          deckName: deck.name,
          opponentLegendId: prep.opponentLegendId,
          opponentLegendName: prep.opponentLegendName,
          label: prep.label,
          first: remapSidePlanIds(prep.first, codeToId),
          second: remapSidePlanIds(prep.second, codeToId),
        });
      }
      deleteImportedPrepDeck(old.id);
      migrated += 1;
    } catch {
      // Keep legacy entry if conversion fails (e.g. legend unresolved).
    }
  }
  return migrated;
}
